import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { Users, Shield, Package, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreorderSection = ({ onPreorderClick }) => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({ total_preorders: 0, total_amount: 0, goal_amount: 25000, percentage: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/preorders/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="preorder" className="calli-section relative overflow-hidden" data-testid="preorder-section">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-yellow-500"></div>
      <div className="absolute inset-0 grid-pattern opacity-20"></div>
      <div className="glow-orb w-[600px] h-[600px] bg-white/10 top-0 right-0"></div>
      
      <div className="calli-container relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 space-y-4">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider bg-white/20 backdrop-blur-sm text-white border border-white/30">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              {language === 'fr' ? 'Précommande ouverte' : 'Pre-orders open'}
            </span>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-white">
              {t("preorder_title")}
            </h2>
            <p className="text-xl text-white/80">{t("preorder_subtitle")}</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-[2rem] p-8 shadow-2xl">
            {/* Progress */}
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-end">
                <div>
                  <span className="text-5xl sm:text-6xl font-black gradient-text">{stats.percentage}%</span>
                  <span className="text-gray-600 ml-2 font-medium">{language === 'fr' ? 'de l\'objectif' : 'of goal'}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-lg text-gray-900">{stats.total_preorders}</span>
                  <span className="text-sm text-gray-600 font-medium ml-1">{t("preorder_backers")}</span>
                </div>
              </div>
              
              <div className="relative h-4 w-full bg-gray-200 rounded-full overflow-hidden" data-testid="progress-bar">
                <div 
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-500 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${Math.max(stats.percentage, 3)}%` }}
                >
                  <div className="absolute inset-0 animate-shimmer"></div>
                </div>
              </div>

              <div className="flex justify-between text-sm">
                <span className="font-bold text-orange-600">{stats.total_amount.toLocaleString()}€ {language === 'fr' ? 'collectés' : 'raised'} 🔥</span>
                <span className="flex items-center gap-2 text-gray-600 font-medium">
                  <Users className="w-4 h-4" />
                  {language === 'fr' ? 'Objectif : 100%' : 'Goal: 100%'}
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="text-center space-y-6 pt-6 border-t border-gray-200">
              <div>
                <div className="inline-flex items-baseline gap-3">
                  <span className="text-7xl sm:text-8xl font-black text-gray-900">15€</span>
                  <span className="text-2xl text-gray-300 line-through">79€</span>
                </div>
                <p className="text-gray-600 font-semibold mt-2">{t("preorder_price_info")}</p>
              </div>

              <button
                onClick={onPreorderClick}
                className="w-full calli-btn-primary text-xl h-16"
                data-testid="preorder-cta"
              >
                {t("preorder_cta")}
                <ArrowRight className="ml-2 w-6 h-6" />
              </button>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
                <span className="flex items-center gap-2 text-green-600 font-semibold">
                  <Shield className="w-5 h-5" />
                  {t("preorder_guarantee")}
                </span>
                <span className="flex items-center gap-2 text-orange-600 font-semibold">
                  <Package className="w-5 h-5" />
                  {t("preorder_delivery")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreorderSection;
